import type DeviceMapEntity from "../../../map-management/map-entities/device-map-entity";

import { OPERATING_MODE } from "../operating-mode/definitions";
import { getOperatingMode } from "../operating-mode/getters";
import { IRRIGATION_STATE } from "./definitions";
import { getClosestIrrigationEventDetails, getPercentageOfIrrigationCompleted, isSomeValveOpen } from "./helpers";

/**
 * Composes data about the current irrigation state of a device
 */
export const getIrrigationDetails = ({ model }: { model: DeviceMapEntity["model"] }) => {
    const hasOpenValve = isSomeValveOpen({ model });
    const { closestEvent, zoneIrrigationStateUncertain, shouldBeIrrigating } = getClosestIrrigationEventDetails({
        model,
    });

    // Find the percentage of irrigation completed for the closest event (not necessarily current one)
    const percentageClosestEventCompleted = closestEvent ? getPercentageOfIrrigationCompleted({ closestEvent }) : null;

    // Find the percentage of current scheduled irrigation completed for the device
    const percentageScheduledIrrigationCompleted = shouldBeIrrigating
        ? Math.round(percentageClosestEventCompleted ?? 0)
        : undefined;

    // Irrigation end time
    const irrigationEndTime = closestEvent ? closestEvent.endTime : undefined;

    return {
        percentageScheduledIrrigationCompleted,
        irrigationEndTime,
        hasOpenValve,
        shouldBeIrrigating,
        zoneIrrigationStateUncertain,
        closestEvent,
    };
};

interface GetValveStatusProps {
    model: DeviceMapEntity["model"];
}
export const getIrrigationState = ({ model }: GetValveStatusProps) => {
    const { hasOpenValve, percentageScheduledIrrigationCompleted } = getIrrigationDetails({ model });
    const { isManualModeTransitioning, targetOperatingMode, operatingMode } = getOperatingMode({ model });

    // Handle the different states of the valve
    if (isManualModeTransitioning && targetOperatingMode === OPERATING_MODE.MANUAL_OPEN) {
        return IRRIGATION_STATE.OPENING;
    }
    if (isManualModeTransitioning && targetOperatingMode === OPERATING_MODE.MANUAL_CLOSE) {
        return IRRIGATION_STATE.CLOSING;
    }
    if (hasOpenValve && percentageScheduledIrrigationCompleted !== undefined) {
        return IRRIGATION_STATE.SCHEDULED_IRRIGATION;
    }
    if (hasOpenValve || operatingMode === OPERATING_MODE.MANUAL_OPEN) {
        return IRRIGATION_STATE.OPEN;
    }
    return IRRIGATION_STATE.CLOSED;
};
